// ===============================================================
// File Library — Search
// Full-text search across all documents
// ===============================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, TextInput, Paper, Badge, Center, Loader,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useFileLibraryStore } from '../../store/fileLibraryStore';
import type { FLDocument, FLCategory } from '../../store/fileLibraryStore';

function getCatColor(catName: string, categories: FLCategory[]): string {
  const cat = categories.find(c => c.name === catName);
  return cat?.color || '#64748B';
}

function getStatusBadge(doc: FLDocument) {
  const status = (doc.status || '').toLowerCase();
  if (status === 'draft') return <Badge size="xs" color="gray">Draft</Badge>;

  if (doc.expirationDate) {
    const exp = new Date(doc.expirationDate);
    if (!isNaN(exp.getTime())) {
      const now = new Date();
      const daysUntil = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) return <Badge size="xs" color="red">Expired</Badge>;
      if (daysUntil < 30) return <Badge size="xs" color="yellow">Expiring Soon</Badge>;
    }
  }

  return <Badge size="xs" color="green">Published</Badge>;
}

export default function Search() {
  const navigate = useNavigate();
  const { documents, categories, loading, loadDocuments, loadCategories } = useFileLibraryStore();
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadDocuments();
    loadCategories();
  }, [loadDocuments, loadCategories]);

  const q = search.toLowerCase().trim();
  const results = q
    ? documents.filter(d =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q) ||
        (d.tags || '').toLowerCase().includes(q) ||
        (d.category || '').toLowerCase().includes(q) ||
        (d.uploadedBy || '').toLowerCase().includes(q)
      )
    : [];

  if (loading) {
    return <Center py="xl"><Loader size="sm" /></Center>;
  }

  return (
    <div>
      <Title order={3} mb="md" c="black">Search Documents</Title>

      <TextInput
        placeholder="Search by title, description, tags, category, or author..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={e => setSearch(e.currentTarget.value)}
        size="md"
        mb="md"
        autoFocus
      />

      {q ? (
        <>
          <Text size="sm" c="dimmed" mb="sm">
            {results.length} result{results.length !== 1 ? 's' : ''} for "{search}"
          </Text>

          {results.length === 0 ? (
            <Paper p="xl" radius="sm" withBorder>
              <Text ta="center" c="dimmed">No documents match your search.</Text>
            </Paper>
          ) : (
            <Paper radius="sm" withBorder style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: 'var(--border)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Title</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Category</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>File Type</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Published</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Expires</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(doc => (
                    <tr
                      key={doc.id}
                      style={{ borderBottom: '1px solid var(--frost)', cursor: 'pointer' }}
                      onClick={() => navigate(`/filelibrary/doc/${doc.id}`)}
                    >
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{doc.title}</td>
                      <td style={{ padding: '8px 12px' }}>
                        {doc.category && (
                          <Badge size="xs" style={{ background: getCatColor(doc.category, categories), color: '#fff' }}>
                            {doc.category}
                          </Badge>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', textTransform: 'uppercase' }}>{doc.fileType}</td>
                      <td style={{ padding: '8px 12px' }}>
                        {doc.publishDate ? new Date(doc.publishDate).toLocaleDateString() : ''}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {doc.expirationDate && !isNaN(new Date(doc.expirationDate).getTime()) ? new Date(doc.expirationDate).toLocaleDateString() : ''}
                      </td>
                      <td style={{ padding: '8px 12px' }}>{getStatusBadge(doc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Paper>
          )}
        </>
      ) : (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">Enter a search term to find documents.</Text>
        </Paper>
      )}
    </div>
  );
}
